import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, Clock, Phone } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { getArticle, getRelated } from "../data/blog";

// Render a paragraph with inline [label](/path) markdown links as internal links.
const InlineText = ({ text }) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [, label, href] = m;
    parts.push(
      <Link key={key++} to={href} className="text-js-blue font-semibold underline decoration-js-blue/30 underline-offset-2 hover:decoration-js-blue transition-colors">
        {label}
      </Link>
    );
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const Block = ({ block }) => {
  if (block.type === "h2")
    return <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-12 mb-4 scroll-mt-24">{block.text}</h2>;
  if (block.type === "h3")
    return <h3 className="font-display font-bold text-xl mt-9 mb-3">{block.text}</h3>;
  if (block.type === "ul")
    return (
      <ul className="my-5 space-y-2.5">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-js-charcoal/75 leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-js-blue shrink-0" />
            <span><InlineText text={it} /></span>
          </li>
        ))}
      </ul>
    );
  return <p className="text-js-charcoal/75 leading-[1.8] my-5 text-[17px]"><InlineText text={block.text} /></p>;
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/blog" replace />;
  const related = getRelated(slug, 3);

  return (
    <div data-testid="article-detail-page">
      <PageHero
        eyebrow={article.category.toUpperCase()}
        title={article.title}
        subtitle={article.subtitle}
        image={article.hero}
        crumbs={[{ label: "Blog", to: "/blog" }, { label: article.category }]}
      />

      {/* META BAR */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <span className="flex items-center gap-2 text-js-charcoal/50 text-sm"><Clock size={15} /> {article.readTime}</span>
          <Link to="/blog" data-testid="back-to-blog-top" className="inline-flex items-center gap-1.5 text-js-blue text-sm font-semibold hover:gap-2.5 transition-all duration-300">
            <ArrowLeft size={15} /> Back to Blog
          </Link>
        </div>
      </div>

      {/* BODY */}
      <article className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            {article.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </Reveal>

          {/* INLINE SERVICE CTA */}
          <Reveal className="mt-12">
            <Link
              to={article.serviceCta.to}
              data-testid="article-service-link"
              className="group flex items-center justify-between gap-4 rounded-2xl bg-js-blue/5 border border-js-blue/15 px-7 py-6 hover:bg-js-blue/10 transition-colors duration-300"
            >
              <span className="font-display font-bold text-lg text-js-charcoal">{article.serviceCta.label}</span>
              <ArrowUpRight size={22} className="text-js-blue shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </article>

      {/* STRONG CTA */}
      <section className="bg-js-charcoal py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <span className="text-js-red font-semibold text-sm tracking-wide">NEED HELP WITH YOUR AIR CONDITIONING?</span>
            <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl tracking-tight mt-4 text-balance">
              We can help — servicing, repairs or installation
            </h2>
            <p className="text-white/60 text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
              Whether you need servicing, repairs, installation or advice on the right system for your property,
              the J&amp;S Air Conditioning team is here to help across Melbourne.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/quote" data-testid="article-cta-quote" className="group flex items-center gap-2 px-8 py-4 rounded-full bg-js-red text-white font-semibold hover:bg-js-red/90 transition-colors duration-300">
                Request a Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link to="/contact" data-testid="article-cta-contact" className="flex items-center gap-2 px-8 py-4 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors duration-300">
                <Phone size={18} /> Contact J&amp;S
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between mb-9">
            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Related articles</h3>
            <Link to="/blog" data-testid="back-to-blog-bottom" className="hidden sm:inline-flex items-center gap-1.5 text-js-blue font-semibold hover:gap-2.5 transition-all duration-300">
              <ArrowLeft size={16} /> Back to Blog
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06}>
                <Link to={`/blog/${a.slug}`} data-testid="related-article-card" className="group flex flex-col bg-js-grey rounded-3xl overflow-hidden border border-black/5 h-full hover:shadow-[0_24px_55px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 transition-all duration-400">
                  <div className="relative h-44 overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-js-blue text-xs font-semibold">{a.category}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h4 className="font-display font-bold text-base leading-snug group-hover:text-js-blue transition-colors duration-300">{a.title}</h4>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-js-blue text-sm font-semibold">
                      Read Article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetail;
